import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { ProjectHeaderName } from "@/api/ApiClientProvider";
import { useApi } from "@/api/hooks/useApi";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";

export const useAddressQueries = () => {
  const projectId = useProjectId();
  const api = useApi();

  const ADDRESS_AMOUNT = 40;

  return {
    list: (query?: { search?: string; addressTypes?: string[]; limit?: number; projectId?: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.ADDRESS_LIST(query?.projectId ?? projectId, query),
        queryFn: () =>
          api.getAddressesV1(
            {
              Search: query?.search,
              AddressType: query?.addressTypes,
              Limit: query?.limit ?? 10000,
            },
            query?.projectId ? { headers: { [ProjectHeaderName]: query.projectId } } : undefined,
          ),
        select: commonAPIDataSelector,
        staleTime: 5 * 60 * 1000, // 5 minutes
      }),
    listInfinite: (query?: { search?: string; addressTypes?: string[] }) =>
      infiniteQueryOptions({
        queryKey: QUERY_KEYS.ADDRESS_LIST_INFINITE(projectId, query),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getAddressesV1({
              Offset: pageParam * ADDRESS_AMOUNT,
              Limit: ADDRESS_AMOUNT,
              Search: query?.search,
              AddressType: query?.addressTypes,
            })
            .then((x) => x.data),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
        staleTime: 5 * 60 * 1000, // 5 minutes
      }),
    details: (id: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.ADDRESS_DETAILS(projectId, id),
        queryFn: () => api.getAddressesDetailsV1(id),
        select: commonAPIDataSelector,
      }),
    stats: () =>
      queryOptions({
        queryKey: QUERY_KEYS.ADDRESS_STATS(projectId),
        queryFn: () => api.getAddressesStatsV1(),
        select: commonAPIDataSelector,
      }),
    addressTypes: () =>
      queryOptions({
        queryKey: QUERY_KEYS.ADDRESS_TYPES(projectId),
        queryFn: () => api.getAddressesTypesV1(),
        select: commonAPIDataSelector,
      }),
  };
};
